import { NavLink } from 'react-router-dom'
import { useAuthStore } from '../../stores/auth.store'
import { cn } from '../../lib/utils'

type NavItem = { label: string; path: string; icon: string }

const ALL_ITEMS: Record<string, NavItem[]> = {
  HEAD_TEACHER: [
    { label: 'Dashboard',    path: '/dashboard',    icon: '🏠' },
    { label: 'Schedule',     path: '/schedule',     icon: '📅' },
    { label: 'Curriculum',   path: '/curriculum',   icon: '📚' },
    { label: 'Lessons',      path: '/lessons',      icon: '📝' },
    { label: 'Homework',     path: '/homework',     icon: '✏️'  },
    { label: 'Assessments',  path: '/assessments',  icon: '📊' },
    { label: 'Children',     path: '/children',     icon: '👦' },
    { label: 'Reports',      path: '/reports',      icon: '📄' },
    { label: 'Merits',       path: '/merits',       icon: '⭐' },
    { label: 'AI Module',    path: '/ai',           icon: '🤖' },
    { label: 'Website',      path: '/website',      icon: '🌐' },
    { label: 'Settings',     path: '/settings',     icon: '⚙️'  },
  ],
  PRINCIPAL: [
    { label: 'Dashboard',  path: '/dashboard',  icon: '🏠' },
    { label: 'Schedule',   path: '/schedule',   icon: '📅' },
    { label: 'Curriculum', path: '/curriculum', icon: '📚' },
    { label: 'Children',   path: '/children',   icon: '👦' },
    { label: 'Reports',    path: '/reports',    icon: '📄' },
    { label: 'Merits',     path: '/merits',     icon: '⭐' },
    { label: 'AI Module',  path: '/ai',         icon: '🤖' },
    { label: 'Website',    path: '/website',    icon: '🌐' },
    { label: 'Settings',   path: '/settings',   icon: '⚙️'  },
  ],
  TUTOR: [
    { label: 'Dashboard',   path: '/dashboard',   icon: '🏠' },
    { label: 'Schedule',    path: '/schedule',    icon: '📅' },
    { label: 'Lessons',     path: '/lessons',     icon: '📝' },
    { label: 'Homework',    path: '/homework',    icon: '✏️'  },
    { label: 'Assessments', path: '/assessments', icon: '📊' },
    { label: 'Settings',    path: '/settings',    icon: '⚙️'  },
  ],
  STUDENT: [
    { label: 'Dashboard', path: '/dashboard', icon: '🏠' },
    { label: 'Schedule',  path: '/schedule',  icon: '📅' },
    { label: 'Homework',  path: '/homework',  icon: '✏️'  },
    { label: 'Merits',    path: '/merits',    icon: '⭐' },
  ],
}

export default function Sidebar({ onClose }: { onClose?: () => void }) {
  const user = useAuthStore((s) => s.user)
  const items = ALL_ITEMS[user?.role ?? 'STUDENT'] ?? []

  return (
    <aside className="w-64 bg-[#1B6B3A] flex flex-col h-screen">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-[#155730]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg overflow-hidden ring-1 ring-white/20 flex-shrink-0">
            <img src="/sprouthill-icon.svg" alt="" className="w-full h-full" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm leading-tight">Sprout Hill</p>
            <p className="text-green-300 text-xs">School LMS</p>
          </div>
          {/* Close button — mobile only */}
          {onClose && (
            <button onClick={onClose} className="lg:hidden text-green-300 hover:text-white text-xl leading-none">×</button>
          )}
        </div>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-white text-[#1B6B3A]'
                  : 'text-green-100 hover:bg-[#155730] hover:text-white',
              )
            }
          >
            <span className="text-base">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Help link + user info at bottom */}
      <div className="px-3 pb-2">
        <NavLink
          to="/help"
          onClick={onClose}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
              isActive
                ? 'bg-white text-[#1B6B3A]'
                : 'text-green-100 hover:bg-[#155730] hover:text-white',
            )
          }
        >
          <span className="text-base">❓</span>
          Help &amp; User Guide
        </NavLink>
      </div>
      <div className="px-4 py-3 border-t border-[#155730]">
        <p className="text-green-100 text-xs truncate">{user?.firstName} {user?.lastName}</p>
        <p className="text-green-300 text-xs capitalize">{user?.role?.toLowerCase().replace('_', ' ')}</p>
      </div>
    </aside>
  )
}

